/**
 * ProductDetailPage — header card (ProductHeader) + 248px Sider rail + content,
 * on the VendorDetailPage trio pattern.
 *
 * S1.2 five-tab shell (plan §S1.2, ux-lens-pinned):
 * - The Sider section menu is REGROUPED into the 5 tab groups (基本資料 →
 *   採購與供應 → 銷售與定價 → 財務資料 → 異動日誌) — a SINGLE nav axis, not a
 *   tab strip layered over a section menu. Each tab renders its sections
 *   stacked; sections keep their own in-page SectionHeader titles (tab labels
 *   never duplicate them — asserted in `__tests__/tabDeepLinks.test.tsx`).
 * - `?tab=` is the ONLY param the UI writes, and it is ROUTE-DERIVED (the menu
 *   writes the param, the param drives the render — house selection tenet).
 *   Legacy `?section=` deep-links are resolved READ-ONLY to owning tab +
 *   anchor scroll (`resolveDetailTab` in ./types); no control writes them.
 * - view/編輯 split retained: chunked Edit*Modals (zodResolver, width 800) per
 *   section; full edit stays on the S1.3 wizard (header Edit).
 * - S2.2 commercial row split: the `commercial` SECTION key renders
 *   `CommercialAccountingRows` under 財務資料; `CommercialSalesRows` mounts
 *   under 銷售與定價 from `COMMERCIAL_ROW_MOUNTS` DATA (one file, two mount
 *   points; flag-free — renders with `engines.pricing` off, outside
 *   PricingSection). Both open the one EditCommercialModal.
 * - S5.1 pricing mount: `PricingSection` mounts under 銷售與定價 from the
 *   `PRICING_SECTION_MOUNT` data, wrapped in `FeatureGate flag="engines.pricing"`
 *   INSIDE the tab with `showDisabled={false}` (hide-don't-disable): flag off ⇒
 *   the gate renders nothing and the tab still shows CommercialSalesRows +
 *   salesAnalytics — never a dead tab.
 * - S6.2 tab assembly (採購與供應): planning → itemLocations → controlCenter
 *   stay grouped in BOARD order (`TAB_SECTION_GROUPS` data, pinned by
 *   `__tests__/controlCenterVisibility.test.tsx`). The board's two vertical
 *   annotation chips (1 Drop Ship / 2 Match Bill to Receipt) resolve onto the
 *   EXISTING planning-toggle surface (FD8 extend-never-duplicate; overlap
 *   resolution favors the existing surface) — their fields sit outside col-J
 *   Must scope (DD!48 = COULD-Sterling, registry tier data only; DD!49/50 =
 *   WON'T DO) and the drop-ship / SO 拆兩行 doctrine is order-domain
 *   (descoped, plan Appendix B#4), so PlanningSection needed NO change and
 *   EditReplenishmentModal stays reachable via its chunk-Edit. The col-J
 *   directive 產品管控中心應該查得到 (DD!183–187) is EXECUTABLE: the quintet
 *   renders as filtered deep-links from `CONTROL_CENTER_DD_ROWS` data and the
 *   visibility test asserts all 5 labels resolve en+zh. Reorder lens untouched
 *   (`reorder.suggestions` gating lives outside this page and stays as-is).
 * - 共用產品 SCN section hosts the retroactive Link/Join/Unlink flow — past
 *   documents keep their references across the whole round-trip.
 * - S8.1 final composition (zero-mockup tabs, FD5 — mapped, never invented):
 *   財務資料 = `CommercialAccountingRows` riding the `commercial` section key
 *   (registry-driven; DD!227–229 render as ordinary could placeholder rows —
 *   their `blocked` flags were lifted in S8.1 together with the wizard zod
 *   coverage, closing the S0.1 handshake — the BlockedFieldCallout path stays
 *   for any future blocked row). 異動日誌 = AuditSection + TransactionsSection
 *   AS-IS in `TAB_SECTION_GROUPS` order; AuditSection additionally surfaces
 *   the PRICE_OVERRIDE review rows from `pricingStore` that belong to THIS
 *   product (pure `selectProductPriceOverrides`). Legacy `?section=audit` /
 *   `?section=transactions` deep-links resolve to 異動日誌 + anchor via the
 *   unchanged read-only resolver (pinned in `__tests__/tabComposition.test.tsx`).
 * - #2168 商品搭配規則: `pairingRules` is now an ordinary `DetailSectionKey`
 *   sitting after `vendors` in `TAB_SECTION_GROUPS.basicInfo`, so the shell's
 *   mount-splice is RETIRED — one section list, one `sectionAnchorId` anchor,
 *   and `?section=pairingRules` resolves through the same read-only resolver
 *   every other section uses (the create wizard hands off with that link).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, Divider, Grid, Layout, Menu, Spin, theme } from 'antd';
import {
  AccountBookOutlined,
  DollarOutlined,
  FileTextOutlined,
  HistoryOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import { ListErrorState } from '@/components/common/TableContainer';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { useProduct, useProductMutations, useScnGroups } from '@/hooks/useProducts';
import { productApi } from '@/services/api';
import { vendorLineWriteReachesTheRead } from '@/services/api/product.api';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import type {
  Product,
  ProductVendorLine,
  UpdateProductVendorLineRequest,
} from '@/types/api/product';
import ProductHeader from './ProductHeader';
import MainInfoSection from './sections/MainInfoSection';
import ClassificationSection from './sections/ClassificationSection';
import DescriptionsSection from './sections/DescriptionsSection';
import ImagesSection from './sections/ImagesSection';
import {
  COMMERCIAL_ROW_MOUNTS,
  CommercialAccountingRows,
  CommercialSalesRows,
} from './sections/CommercialSection';
import PlanningSection from './sections/PlanningSection';
import PricingSection, { PRICING_SECTION_MOUNT } from './sections/PricingSection';
import { FeatureGate } from '@/features/flags/FeatureGate';
import { useFeature } from '@/features/flags';
import VendorLinesSection from './sections/VendorLinesSection';
import PairingRulesSection from './sections/PairingRulesSection';
import ItemLocationsSection from './sections/ItemLocationsSection';
import SalesAnalyticsSection from './sections/SalesAnalyticsSection';
import ScnSection from './sections/ScnSection';
import TransactionsSection from './sections/TransactionsSection';
import ControlCenterLinksSection from './sections/ControlCenterLinksSection';
import AuditSection from './sections/AuditSection';
import EditMainInfoModal from './modals/EditMainInfoModal';
import EditClassificationModal from './modals/EditClassificationModal';
import EditDescriptionsModal from './modals/EditDescriptionsModal';
import EditCommercialModal from './modals/EditCommercialModal';
import AddVendorLineModal from './modals/AddVendorLineModal';
import ScnLinkDrawer from './ScnLinkDrawer';
import {
  DEFAULT_DETAIL_TAB,
  DETAIL_TAB_KEYS,
  TAB_SECTION_GROUPS,
  buildDetailTabSearchParams,
  resolveDetailTab,
  sectionAnchorId,
  sectionOwningTab,
} from './types';
import type { DetailSectionKey, ProductDetailTabKey, ProductEditModalKey } from './types';
import PageContainer from '@/components/common/PageContainer';

const { Sider, Content } = Layout;

/** Rail icons per tab (board fidelity gap on the duplicate $ — distinct here). */
const TAB_ICONS: Record<ProductDetailTabKey, React.ReactNode> = {
  basicInfo: <FileTextOutlined />,
  purchasingSupply: <ShoppingCartOutlined />,
  salesPricing: <DollarOutlined />,
  financialInfo: <AccountBookOutlined />,
  changeLog: <HistoryOutlined />,
};

/** Full literal keys (i18n:audit-visible), resolved via t() at render. */
const TAB_LABEL_KEYS: Record<ProductDetailTabKey, string> = {
  basicInfo: 'products:detail.tabs.basicInfo',
  purchasingSupply: 'products:detail.tabs.purchasingSupply',
  salesPricing: 'products:detail.tabs.salesPricing',
  financialInfo: 'products:detail.tabs.financialInfo',
  changeLog: 'products:detail.tabs.changeLog',
};

const ProductDetailPage = () => {
  const { t } = useTranslation(['products', 'common']);
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const elevation = useElevation();
  const screens = Grid.useBreakpoint();

  // ?tab= route-derived selection; legacy ?section= resolved read-only.
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab: activeTab, anchor: legacyAnchor } = resolveDetailTab(
    searchParams.get('tab'),
    searchParams.get('section')
  );

  const setActiveTab = (tab: ProductDetailTabKey) => {
    // The ONLY params write on this page — drops any legacy ?section=.
    setSearchParams(buildDetailTabSearchParams(tab));
  };

  const { product, isLoading, error, refresh } = useProduct(productId);
  const { groups, isLoading: groupsLoading } = useScnGroups();
  const { setActive, unlinkScn, setPrimaryVendor } = useProductMutations(productId);

  const [editModal, setEditModal] = useState<ProductEditModalKey | null>(null);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [scnDrawerOpen, setScnDrawerOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  // PROPOSED (RULING-3 2026-08-29) — pending 林芊 EN sign-off + Melissa mock
  // walk: the commercial-surface copy + one-edit-entry variant. The flag is
  // read HERE only; CommercialSection stays flag-token-free (S2.2 pin) and
  // receives the decision as a prop. Flag off ⇒ 'legacy', byte-identical.
  const pricingOn = useFeature('engines.pricing');
  const commercialVariant = pricingOn ? ('engine' as const) : ('legacy' as const);

  // Anchor scrolling: legacy ?section= fires once per param value; in-page
  // cross-section links (MainInfoSection) queue a pendingAnchor locally —
  // they navigate by writing ?tab= only, never ?section=.
  const [pendingAnchor, setPendingAnchor] = useState<DetailSectionKey | null>(null);
  const scrolledLegacyAnchorRef = useRef<DetailSectionKey | null>(null);

  const toggleStatusDialog = useConfirmationDialog<Product>();
  const unlinkScnDialog = useConfirmationDialog<Product>();

  const scnMembers = useMemo(() => {
    if (!product?.scn) return [];
    const group = groups.find((g) => g.scn === product.scn);
    return (group?.members ?? []).filter((member) => member.id !== product.id);
  }, [groups, product]);

  const menuItems = DETAIL_TAB_KEYS.map((key) => ({
    key,
    icon: TAB_ICONS[key],
    label: t(TAB_LABEL_KEYS[key]),
  }));

  /** In-page section jump — resolves the owning tab, then anchor-scrolls. */
  const navigateToSection = (section: DetailSectionKey) => {
    const owningTab = sectionOwningTab(section);
    if (owningTab !== activeTab) setActiveTab(owningTab);
    setPendingAnchor(section);
  };

  // R3 one-edit-entry link (財務資料 → Sales Controls): the sales mount is
  // deliberately OUTSIDE the section registry (S2.2), so it gets its own
  // pending flag + anchor id instead of extending DetailSectionKey.
  const [pendingSalesAnchor, setPendingSalesAnchor] = useState(false);
  const goToSalesControls = () => {
    if (activeTab !== COMMERCIAL_ROW_MOUNTS.sales) setActiveTab(COMMERCIAL_ROW_MOUNTS.sales);
    setPendingSalesAnchor(true);
  };
  useEffect(() => {
    if (!pendingSalesAnchor || activeTab !== COMMERCIAL_ROW_MOUNTS.sales) return;
    document.getElementById('product-sales-controls')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    setPendingSalesAnchor(false);
  }, [pendingSalesAnchor, activeTab]);

  const anchorTimersRef = useRef<number[]>([]);
  useEffect(() => {
    if (!product) return;
    const legacyDue =
      legacyAnchor && scrolledLegacyAnchorRef.current !== legacyAnchor ? legacyAnchor : null;
    const anchor = pendingAnchor ?? legacyDue;
    if (!anchor || sectionOwningTab(anchor) !== activeTab) return;
    anchorTimersRef.current.forEach((id) => window.clearTimeout(id));
    anchorTimersRef.current = [];
    if (pendingAnchor) {
      // In-page jump: target content is already mounted — one smooth scroll.
      document.getElementById(sectionAnchorId(anchor))?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setPendingAnchor(null);
      return;
    }
    // Legacy ?section= deep-link: sections above the anchor load async and
    // keep expanding the layout right after first paint (a smooth scroll gets
    // aborted / lands stale), so re-anchor INSTANTLY over a short settle
    // window, no-oping once the anchor sits at the top. Timers live in a ref
    // (not effect cleanup) so a benign product-identity refresh doesn't cancel
    // them; a tab switch unmounts the anchor and the ticks no-op.
    scrolledLegacyAnchorRef.current = legacyDue;
    anchorTimersRef.current = [0, 300, 700, 1200, 2000].map((delay) =>
      window.setTimeout(() => {
        const el = document.getElementById(sectionAnchorId(anchor));
        if (!el) return;
        const { top } = el.getBoundingClientRect();
        if (top >= 0 && top <= 80) return; // already anchored (scroll-margin 16)
        el.scrollIntoView({ block: 'start' });
      }, delay)
    );
  }, [pendingAnchor, legacyAnchor, activeTab, product]);
  useEffect(
    () => () => {
      // Unmount (incl. StrictMode's simulated one): drop pending re-anchors and
      // reset the once-per-value guard so a remount deep-link anchors again.
      anchorTimersRef.current.forEach((id) => window.clearTimeout(id));
      anchorTimersRef.current = [];
      scrolledLegacyAnchorRef.current = null;
    },
    []
  );

  const handleToggleStatus = async () => {
    if (!product) return;
    setToggling(true);
    try {
      await setActive(!product.isActive);
      message.success(
        t(product.isActive ? 'products:messages.deactivated' : 'products:messages.activated')
      );
      refresh();
    } catch {
      message.error(t('common:messages.updateFailed'));
    } finally {
      setToggling(false);
      toggleStatusDialog.close();
    }
  };

  const handleUnlinkScn = async () => {
    try {
      await unlinkScn();
      message.success(t('products:detail.scn.unlinkedMessage'));
      refresh();
    } catch {
      message.error(t('common:messages.updateFailed'));
    } finally {
      unlinkScnDialog.close();
    }
  };

  // Vendor-line capability gate (US-148 生效/停用 + US-266 新增/設為主要). Not a
  // permission and not a loading state: NO vendor-line write has a graph root
  // while the line READ is graph-served, so in that posture none of the three
  // can be read back. One predicate, one gate — a per-control gate is how two
  // of the three came to be rendered ungated in the first place.
  const canWriteVendorLine = vendorLineWriteReachesTheRead();

  // US-266 — 設為主要供應商. Facade-routed like its two siblings, and HIDDEN on
  // the same predicate: `isPrimary` is on the graph-served detail selection
  // set, so a mock POST moves nothing this page can render. Round 4 measured
  // this handler firing its success toast over an unmoved Postgres row.
  const handleSetPrimary = async (line: ProductVendorLine) => {
    try {
      await setPrimaryVendor(line.vendorId);
      message.success(t('products:messages.preferredUpdated'));
      refresh();
    } catch {
      message.error(t('common:messages.updateFailed'));
    }
  };

  // US-148 — per-line 生效/停用 toggle. Routed through the facade (register
  // §0g) so it consults the seam instead of PATCHing whatever answers. The
  // action is HIDDEN when the write cannot reach the read — see the predicate
  // and `canWriteVendorLine` above; this handler only ever runs when it can.
  const handleToggleVendorLine = async (line: ProductVendorLine) => {
    if (!product) return;
    try {
      const body: UpdateProductVendorLineRequest = { isActive: line.isActive === false };
      await productApi.setVendorLineActive(product.id, line.id, body);
      message.success(t('products:vendorLine.statusUpdated'));
      refresh();
    } catch {
      message.error(t('common:messages.updateFailed'));
    }
  };

  if (error) {
    return (
      <PageContainer>
        <Card style={{ ...elevation.card }}>
          <ListErrorState
            title={t('products:states.loadFailed')}
            retryLabel={t('common:errors.boundary.retry')}
            onRetry={refresh}
          />
        </Card>
      </PageContainer>
    );
  }

  const renderSection = (section: DetailSectionKey, product: Product) => {
    switch (section) {
      case 'main':
        return (
          <MainInfoSection
            product={product}
            onEdit={() => setEditModal('main')}
            onNavigateSection={navigateToSection}
          />
        );
      case 'classification':
        return (
          <ClassificationSection product={product} onEdit={() => setEditModal('classification')} />
        );
      case 'descriptions':
        return (
          <DescriptionsSection product={product} onEdit={() => setEditModal('descriptions')} />
        );
      case 'images':
        // V3-S2 — self-contained (PlanningSection precedent): own modal +
        // PATCH write via useProductMutations; refresh through onSaved.
        return <ImagesSection product={product} onSaved={refresh} />;
      case 'commercial':
        // S2.2 — the section key carries only the ACCOUNTING rows now; the
        // sales rows mount separately under COMMERCIAL_ROW_MOUNTS.sales.
        return (
          <CommercialAccountingRows
            product={product}
            variant={commercialVariant}
            onEdit={() => setEditModal('commercial')}
            onGoToSalesPricing={goToSalesControls}
          />
        );
      case 'planning':
        // SR0.2 — self-contained (hosts its own EditReplenishmentModal).
        return <PlanningSection product={product} onSaved={refresh} />;
      case 'pairingRules':
        // #2168 (G-2166-K-04) — self-contained (PlanningSection precedent):
        // own fetch + modal + refresh. Ordinary registry section since the
        // promotion, so it needs no mount splice and no private anchor.
        return <PairingRulesSection product={product} />;
      case 'vendors':
        return (
          // ALL THREE gated on the SAME fact, because they have the same
          // cause. Absent ⇒ the section hides that control and says why once.
          // Hide, don't disable — and never offer a control whose success is a
          // fiction. Passing two of these unconditionally beside a gated third
          // is exactly what round 4 found here.
          <VendorLinesSection
            product={product}
            onAdd={canWriteVendorLine ? () => setAddVendorOpen(true) : undefined}
            onSetPrimary={canWriteVendorLine ? handleSetPrimary : undefined}
            onToggleActive={canWriteVendorLine ? handleToggleVendorLine : undefined}
          />
        );
      case 'scn':
        return (
          <ScnSection
            product={product}
            members={scnMembers}
            membersLoading={groupsLoading}
            onLink={() => setScnDrawerOpen(true)}
            onUnlink={() => unlinkScnDialog.open(product)}
          />
        );
      case 'itemLocations':
        // SR3.2 — read-only NetSuite availability read-through (self-contained).
        return <ItemLocationsSection product={product} />;
      case 'salesAnalytics':
        // SR3.3 — Tier-2 sales/margin aggregates via the shared selector.
        return <SalesAnalyticsSection product={product} />;
      case 'transactions':
        return <TransactionsSection product={product} />;
      case 'controlCenter':
        // SR3.5/S6.2 — the DD!183–187 產品管控中心 quintet as filtered
        // deep-links (no new subtabs); executable visibility contract in
        // `__tests__/controlCenterVisibility.test.tsx`.
        return <ControlCenterLinksSection product={product} />;
      case 'audit':
        return <AuditSection product={product} />;
    }
  };

  return (
    <PageContainer>
      <Spin spinning={isLoading}>
        {product && (
          <>
            <ProductHeader
              product={product}
              toggling={toggling}
              showReadinessAlert={activeTab !== DEFAULT_DETAIL_TAB}
              onEdit={() => navigate(`/auth/products/${product.id}/edit`)}
              onToggleStatus={() => toggleStatusDialog.open(product)}
            />

            <Card
              style={{
                ...elevation.card,
                overflow: 'hidden',
              }}
              styles={{ body: { padding: 0 } }}
            >
              <Layout style={{ backgroundColor: 'transparent' }}>
                {screens.lg && (
                  <Sider
                    width={248}
                    theme="light"
                    style={{
                      borderRight: `1px solid ${token.colorBorderSecondary}`,
                      padding: 0,
                      backgroundColor: 'transparent',
                    }}
                  >
                    <style>
                      {`
                        .product-detail-menu {
                          padding: 0 12px !important;
                        }
                        .product-detail-menu .ant-menu-item {
                          height: ${controlHeightTokens.controlHeightXL}px !important;
                          line-height: ${controlHeightTokens.controlHeightXL}px !important;
                          margin: 4px 0 !important;
                          display: flex !important;
                          align-items: center !important;
                          border-radius: ${radiusTokens.card}px !important;
                          width: 100% !important;
                        }
                      `}
                    </style>
                    <Menu
                      mode="inline"
                      selectedKeys={[activeTab]}
                      items={menuItems}
                      onClick={({ key }) => setActiveTab(key as ProductDetailTabKey)}
                      className="product-detail-menu"
                      style={{ borderRight: 'none', backgroundColor: 'transparent' }}
                    />
                  </Sider>
                )}
                <Content style={{ padding: '24px', minHeight: '400px', minWidth: 0 }}>
                  {!screens.lg && (
                    <Menu
                      mode="horizontal"
                      selectedKeys={[activeTab]}
                      items={menuItems}
                      onClick={({ key }) => setActiveTab(key as ProductDetailTabKey)}
                      style={{ marginBottom: token.marginLG }}
                    />
                  )}
                  {/* S5.1 pricing mount (data-driven via PRICING_SECTION_MOUNT)
                      — FIRST in board order under 銷售與定價, gated INSIDE the
                      tab: flag off ⇒ the gate renders NOTHING (hide-don't-
                      disable) and the tab still shows CommercialSalesRows +
                      salesAnalytics below — never a dead tab. */}
                  {activeTab === PRICING_SECTION_MOUNT && (
                    <FeatureGate flag="engines.pricing" showDisabled={false}>
                      <PricingSection product={product} />
                      <Divider style={{ margin: '32px 0' }} />
                    </FeatureGate>
                  )}
                  {/* S2.2 sales-row mount point (data-driven, one of two for
                      the commercial row split) — below the W5 PricingSection
                      mount, flag-free so the tab has content with
                      engines.pricing off. */}
                  {activeTab === COMMERCIAL_ROW_MOUNTS.sales && (
                    /* R3: anchor target for the 財務資料 go-to link — the
                       sales mount stays outside the section registry. */
                    <div id="product-sales-controls" style={{ scrollMarginTop: 16 }}>
                      <CommercialSalesRows
                        product={product}
                        variant={commercialVariant}
                        onEdit={() => setEditModal('commercial')}
                      />
                      <Divider style={{ margin: '32px 0' }} />
                    </div>
                  )}
                  {/* The active tab's sections, stacked in board order; each
                      wrapper is the anchor target for ?section= deep-links. */}
                  {TAB_SECTION_GROUPS[activeTab].map((section, index) => (
                    <div
                      key={section}
                      id={sectionAnchorId(section)}
                      style={{ scrollMarginTop: 16 }}
                    >
                      {index > 0 && <Divider style={{ margin: '32px 0' }} />}
                      {renderSection(section, product)}
                    </div>
                  ))}
                </Content>
              </Layout>
            </Card>

            {/* Chunked edit modals (zodResolver, width 800) */}
            <EditMainInfoModal
              open={editModal === 'main'}
              product={product}
              onCancel={() => setEditModal(null)}
              onSaved={refresh}
            />
            <EditClassificationModal
              open={editModal === 'classification'}
              product={product}
              onCancel={() => setEditModal(null)}
              onSaved={refresh}
              // ROW 22 — duplicate-OEM 選擇式關聯 routes into the SCN drawer.
              onOpenScnDrawer={() => setScnDrawerOpen(true)}
            />
            <EditDescriptionsModal
              open={editModal === 'descriptions'}
              product={product}
              onCancel={() => setEditModal(null)}
              onSaved={refresh}
            />
            <EditCommercialModal
              open={editModal === 'commercial'}
              product={product}
              onCancel={() => setEditModal(null)}
              onSaved={refresh}
            />
            <AddVendorLineModal
              open={addVendorOpen}
              product={product}
              onCancel={() => setAddVendorOpen(false)}
              onSaved={refresh}
            />
            <ScnLinkDrawer
              open={scnDrawerOpen}
              product={product}
              onClose={() => setScnDrawerOpen(false)}
              onLinked={refresh}
            />

            <ConfirmationDialog
              open={toggleStatusDialog.isOpen}
              onClose={toggleStatusDialog.close}
              onConfirm={handleToggleStatus}
              title={
                product.isActive ? t('products:deactivateConfirm') : t('products:activateConfirm')
              }
              message={
                product.isActive
                  ? t('products:deactivateDetail', { name: product.displayName })
                  : t('products:activateDetail', { name: product.displayName })
              }
              type={product.isActive ? 'danger' : 'info'}
              confirmText={product.isActive ? t('products:deactivate') : t('products:activate')}
              loading={toggling}
            />

            <ConfirmationDialog
              open={unlinkScnDialog.isOpen}
              onClose={unlinkScnDialog.close}
              onConfirm={handleUnlinkScn}
              title={t('products:detail.scn.unlinkConfirmTitle')}
              message={t('products:detail.scn.unlinkConfirmMessage', {
                scn: product.scn ?? '',
              })}
              type="danger"
              confirmText={t('products:detail.scn.unlink')}
            />
          </>
        )}
      </Spin>
    </PageContainer>
  );
};

export default ProductDetailPage;
